import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

/**
 * AuthProvider con SLIDING SESSION:
 *  - Access token con TTL 30 giorni (lato backend).
 *  - Refresh silenzioso ogni 15 minuti via POST /api/auth/refresh (riporta exp a +30gg).
 *  - Refresh anche al mount, al focus della finestra e appena torna la connessione.
 *  - LOGOUT **solo** su 401/403 del server; errori di rete (offline, 5xx, timeout, CORS) NON scadono la sessione.
 *  - Il token viene letto da localStorage; se un altro tab lo aggiorna, questo tab se ne accorge via `storage` event.
 */
const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 min
const AUTH_HEADER = 'Authorization';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const justLoggedInRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const refreshTimerRef = useRef(null);

  const applyToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common[AUTH_HEADER] = `Bearer ${newToken}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common[AUTH_HEADER];
    }
  }, []);

  const logout = useCallback(() => {
    applyToken(null);
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
  }, [applyToken]);

  // Silent refresh. Ritorna true se andato, false altrimenti.
  const silentRefresh = useCallback(async () => {
    if (refreshInFlightRef.current) return false;
    const current = localStorage.getItem('token');
    if (!current) return false;
    refreshInFlightRef.current = true;
    try {
      const r = await axios.post(`${API}/auth/refresh`, null, {
        headers: { [AUTH_HEADER]: `Bearer ${current}` },
      });
      const newToken = r.data?.access_token;
      if (newToken) {
        applyToken(newToken);
        setToken(newToken);
        if (r.data?.user) setUser(r.data.user);
        return true;
      }
      return false;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // token davvero invalido/scaduto: logout
        console.warn('[Auth] refresh 401/403 → logout');
        logout();
      } else {
        // Errore di rete transitorio: NON logout. Riproveremo al prossimo tick.
        console.info('[Auth] refresh fallito (rete), riprovo al prossimo tick:', err?.message);
      }
      return false;
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [applyToken, logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        console.warn('[Auth] /me →', status, '→ logout');
        logout();
      } else {
        console.info('[Auth] /me fallito ma token ancora valido — mantengo sessione:', error?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Setup iniziale + timer refresh
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios.defaults.headers.common[AUTH_HEADER] = `Bearer ${token}`;
    if (justLoggedInRef.current) {
      justLoggedInRef.current = false;
      setLoading(false);
    } else {
      fetchUser();
    }
    // Timer sliding renewal
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => { silentRefresh(); }, REFRESH_INTERVAL_MS);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token, fetchUser, silentRefresh]);

  // Refresh anche: 1) quando la pagina riprende focus 2) appena torna online
  useEffect(() => {
    const onFocus = () => { if (localStorage.getItem('token')) silentRefresh(); };
    const onOnline = () => { if (localStorage.getItem('token')) silentRefresh(); };
    const onStorage = (e) => {
      if (e.key === 'token') {
        const t = e.newValue;
        if (!t) { logout(); return; }
        if (t !== token) {
          applyToken(t);
          setToken(t);
        }
      }
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('storage', onStorage);
    };
  }, [token, silentRefresh, applyToken, logout]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    applyToken(access_token);
    justLoggedInRef.current = true;
    setUser(userData);
    setToken(access_token);
    return userData;
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isCompanyAdmin = () => user?.role === 'company_admin' || user?.role === 'super_admin';
  const isSlave = () => Boolean(user?.is_slave);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      logout,
      loading,
      isSuperAdmin,
      isCompanyAdmin,
      isSlave,
      silentRefresh,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
